import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Typography from '@material-ui/core/Typography';
import Desc from '../FormWidgets/Description';
import { Collapse } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Button from '@material-ui/core/Button';
import { isMobile } from 'react-device-detect';

import { ShowSetting } from '../FormWidgets/ShowSliderSetting';
import { SettingGroupGrid, ValueBox } from '../FormWidgets/SettingGroup';
import { LoadSettingsDialog } from '../Utils/load_settings';

import { settingControls, useSavedSettings } from './settingFunctions';
import { SettingActions } from '../components';
import { useTranslation } from 'react-i18next';
import { FlexSelect } from '../FormWidgets/Select';
import { nutritionPumpTypeMapping, nutritionPumpUnitMapping } from '../Constants/constants';

import { ExpandMore } from '../Utils/Expand';
import TabContext from '@material-ui/lab/TabContext';
import TabList from '@material-ui/lab/TabList';
import TabPanel from '@material-ui/lab/TabPanel';
import Box from '@material-ui/core/Box';
import { AppBar, Tab, Grid, makeStyles } from '@material-ui/core';
import { EditableLabel } from '../FormWidgets/EditableLabel';
import _ from 'lodash';

const useStyles = makeStyles((theme) => ({
  icon: {
    color: theme.palette.text.primary
  }
}));

export default function MultipleNutrientSettings({ controller, parent, ...other }) {
  let { tab_id } = useParams();
  let { t } = useTranslation();
  const [mobile, setMobile] = useState(isMobile);
  const [values, setValues] = useState(_.cloneDeep(parent.settings[tab_id]));
  const controls = settingControls(controller, parent, tab_id, values, setValues);
  const { openLoadSettingsDialog, closeLoadSettingsDialog } = useSavedSettings(parent, controls);

  const write_access = controller.access_rights.nutrition.write;

  const addPump = (id) => {
    if (values.nutrient_settings[id].pump_cnt_used < 8) {
      let pumps = [...values.nutrient_settings[id].pumps];
      pumps.push({ type: 0, unit: 0, value: 0, name: '' });

      controls.setValue(
        [`nutrient_settings[${id}].pump_cnt_used`, `nutrient_settings[${id}].pumps`],
        [values.nutrient_settings[id].pump_cnt_used + 1, pumps]
      );
    }
  };
  const delLastPump = (id) => {
    if (values.nutrient_settings[id].pump_cnt_used > 1) {
      let pumps = [...values.nutrient_settings[id].pumps];
      pumps.pop();
      controls.setValue(
        [`nutrient_settings[${id}].pump_cnt_used`, `nutrient_settings[${id}].pumps`],
        [values.nutrient_settings[id].pump_cnt_used - 1, pumps]
      );
    }
  };

  // Tab handling for nutrient settings
  const nutrientIds = Object.keys(values.nutrient_settings);
  const [activeNutrient, setActiveNutrient] = useState(nutrientIds[0] || '');
  useEffect(() => {
    if (!nutrientIds.includes(activeNutrient) && nutrientIds.length) {
      setActiveNutrient(nutrientIds[0]);
    }
  }, [nutrientIds, activeNutrient]);

  return (
    <React.Fragment>
      <LoadSettingsDialog
        open={openLoadSettingsDialog}
        handleClose={closeLoadSettingsDialog}
        controls={controls}
      />

      <TabContext value={activeNutrient}>
        <AppBar
          position="static"
          color="default"
          style={
            mobile
              ? { margin: '-24px -24px 48px', width: 'calc(100% + 48px)' }
              : { margin: '-24px -224px 48px', width: 'calc(100% + 448px)' }
          }
        >
          <Box display="flex" justifyContent="center" width="100%">
            <TabList
              variant="scrollable"
              scrollButtons="auto"
              onChange={(_, v) => setActiveNutrient(v)}
            >
              {nutrientIds.map((nid, idx) => (
                <Tab
                  key={nid}
                  value={nid}
                  label={`${t('nutrition')} ${idx + 1}`}
                  id={`nutrient-tab-${idx}`}
                  aria-controls={`nutrient-tabpanel-${idx}`}
                />
              ))}
            </TabList>
          </Box>
        </AppBar>

        {write_access && (
          <SettingActions onReset={controls.resetSettings} onSave={controls.saveSettings} />
        )}

        {nutrientIds.map((id, idx) => (
          <TabPanel key={id} value={id} id={`nutrient-tabpanel-${idx}`} style={{ padding: 0 }}>
            <SettingGroupGrid controller={controller} id={'nutrient_solution'}>
              <ShowSetting
                id={`nutrient_settings[${id}].ec_setpoint`}
                show={'ec_setpoint'}
                controller={controller}
                cur_value={values.nutrient_settings[id].ec_setpoint}
                controller_value={parent.settings.nutrition.nutrient_settings[id].ec_setpoint}
                controls={controls}
                disabled={!write_access}
                type="float"
                num_digits={2}
              />
              <ShowSetting
                id={`nutrient_settings[${id}].ph_setpoint`}
                show={'ph_setpoint'}
                controller={controller}
                cur_value={values.nutrient_settings[id].ph_setpoint}
                controller_value={parent.settings.nutrition.nutrient_settings[id].ph_setpoint}
                controls={controls}
                disabled={!write_access}
                type="float"
                num_digits={2}
              />
              <ShowSetting
                id={`nutrient_settings[${id}].temp_setpoint`}
                show={'temp_setpoint'}
                controller={controller}
                cur_value={values.nutrient_settings[id].temp_setpoint}
                controller_value={parent.settings.nutrition.nutrient_settings[id].temp_setpoint}
                controls={controls}
                disabled={!write_access}
                type="float"
              />
            </SettingGroupGrid>

            {values.nutrient_settings[id].pumps.map((pump, i) => (
              <Pump
                id={id}
                index={i}
                name={pump.name}
                values={values}
                controller={controller}
                parent={parent}
                value_path_prefix={`nutrient_settings[${id}].`}
                controls={controls}
                write_access={write_access}
              />
            ))}
            <Grid container spacing={1} columns={{ xs: 6, md: 12 }}>
              <Grid item xs={4}></Grid>
              <Grid item xs={2}>
                <Button
                  color="primary"
                  onClick={() => {
                    addPump(id);
                  }}
                  disabled={values.nutrient_settings[id].pump_cnt_used >= 8 || !write_access}
                >
                  {t('add_pump')}
                </Button>
              </Grid>
              <Grid item xs={2}>
                <Button
                  color="primary"
                  onClick={() => {
                    delLastPump(id);
                  }}
                  disabled={values.nutrient_settings[id].pump_cnt_used <= 1 || !write_access}
                >
                  {t('del_last_pump')}
                </Button>
              </Grid>
            </Grid>
          </TabPanel>
        ))}
      </TabContext>

      {write_access && (
        <SettingActions onReset={controls.resetSettings} onSave={controls.saveSettings} />
      )}
    </React.Fragment>
  );
}

export function NutrientSettings({ controller, parent, ...other }) {
  let { tab_id } = useParams();
  let { t } = useTranslation();
  const [values, setValues] = useState(_.cloneDeep(parent.settings[tab_id]));
  const controls = settingControls(controller, parent, tab_id, values, setValues);
  const { openLoadSettingsDialog, closeLoadSettingsDialog } = useSavedSettings(parent, controls);

  const write_access = controller.access_rights.nutrition.write;

  const addPump = () => {
    if (values.pump_cnt_used < 8) {
      let pumps = [...values.pumps];
      pumps.push({ type: 0, unit: 0, value: 0, name: '' });

      controls.setValue(['pump_cnt_used', 'pumps'], [values.pump_cnt_used + 1, pumps]);
    }
  };
  const delLastPump = () => {
    if (values.pump_cnt_used > 1) {
      let pumps = values.pumps;
      pumps.pop();
      controls.setValue(['pump_cnt_used', 'pumps'], [values.pump_cnt_used - 1, pumps]);
    }
  };

  return (
    <React.Fragment>
      <LoadSettingsDialog
        open={openLoadSettingsDialog}
        handleClose={closeLoadSettingsDialog}
        controls={controls}
      />

      {write_access && (
        <SettingActions onReset={controls.resetSettings} onSave={controls.saveSettings} />
      )}

      <SettingGroupGrid controller={controller} id={'nutrient_solution'}>
        <ShowSetting
          id="ec_setpoint"
          controller={controller}
          cur_value={values.ec_setpoint}
          controller_value={parent.settings.nutrition.ec_setpoint}
          controls={controls}
          disabled={!write_access}
          type="float"
          num_digits={2}
        />
        <ShowSetting
          id="ph_setpoint"
          controller={controller}
          cur_value={values.ph_setpoint}
          controller_value={parent.settings.nutrition.ph_setpoint}
          controls={controls}
          disabled={!write_access}
          type="float"
          num_digits={2}
        />
        <ShowSetting
          id="temp_setpoint"
          controller={controller}
          cur_value={values.temp_setpoint}
          controller_value={parent.settings.nutrition.temp_setpoint}
          controls={controls}
          disabled={!write_access}
          type="float"
        />
      </SettingGroupGrid>

      {values.pumps.map((pump, i) => (
        <Pump
          index={i}
          controller={controller}
          parent={parent}
          values={values}
          controls={controls}
          write_access={write_access}
        />
      ))}
      <Grid container spacing={1} columns={{ xs: 6, md: 12 }}>
        <Grid item xs={4}></Grid>
        <Grid item xs={2}>
          <Button
            color="primary"
            onClick={() => {
              addPump();
            }}
            disabled={values.pump_cnt_used >= 8 || !write_access}
          >
            {t('add_pump')}
          </Button>
        </Grid>
        <Grid item xs={2}>
          <Button
            color="primary"
            onClick={() => {
              delLastPump();
              controls.setValue('pump_cnt_used', values.pump_cnt_used - 1);
            }}
            disabled={values.pump_cnt_used <= 1 || !write_access}
          >
            {t('del_last_pump')}
          </Button>
        </Grid>
      </Grid>

      {write_access && (
        <SettingActions onReset={controls.resetSettings} onSave={controls.saveSettings} />
      )}
    </React.Fragment>
  );
}

const default_pump = { name: '', type: 0, value: 0, unit: 0 };

export function Pump({
  id,
  index,
  name = '',
  values,
  controls,
  controller,
  parent,
  write_access,
  value_path_prefix = '',
  edit_name = true
}) {
  let { t } = useTranslation();
  const classes = useStyles();

  let p_path = `${value_path_prefix}pumps[${index}]`;
  let pump = _.get(values, p_path, {});
  pump = { ...default_pump, ...pump };

  const [expand, setExpand] = useState(controls.hasChanged(p_path));

  useEffect(() => {
    setExpand(expand || controls.hasChanged(`pumps[${index}]`));
  }, [values]);

  return (
    <SettingGroupGrid label={t('pump') + ' ' + (index + 1)} fullWidth={true}>
      <Grid container>
        <Grid item xs={4}>
          {edit_name ? (
            <EditableLabel
              label={'my_memo'}
              id={`pump[${index}].name`}
              value={pump.name ?? -1}
              handleInput={(_, event, value) => controls.setValue(`${p_path}.name`, value)}
              no_label={true}
            />
          ) : (
            <>{name}</>
          )}
        </Grid>
        <Grid item xs={4}>
          {t(nutritionPumpTypeMapping[pump.type] ?? nutritionPumpTypeMapping['default'])}
        </Grid>
      </Grid>

      <div style={{ textAlign: 'center', marginTop: '-5em', marginLeft: '45%', width: '10%' }}>
        <ExpandMore
          expand={expand}
          onClick={() => setExpand(!expand)}
          aria-expanded={expand[index]}
          aria-label="show more"
        >
          <ExpandMoreIcon fontSize="large" className={classes.icon} />
        </ExpandMore>
      </div>

      <Collapse in={expand} timeout="auto" unmountOnExit>
        <Grid container spacing={1} columns={{ xs: 6, md: 12 }}>
          <Grid item xs={12} md={6}>
            <ShowSetting
              id={`${p_path}.value` /* used as key for controls.setValue() */}
              show="pump_dosage"
              unit_key={'pumps.value'}
              controller={controller}
              cur_value={pump.value}
              changed={controls.hasChanged(`${p_path}.value`)}
              controls={controls}
              unit={t(nutritionPumpUnitMapping[pump.unit] ?? nutritionPumpUnitMapping['default'])}
              disabled={pump.type === 0 || pump.type >= 2 || !write_access}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <ValueBox controller={controller} id={'type'}>
              <Typography gutterBottom>
                <Desc id="pump_type" />
              </Typography>
              <FlexSelect
                id={`pump[${index}].type`}
                controller={controller}
                value={pump.type}
                controller_value={
                  controller.zones
                    ? parent.settings.nutrition.nutrient_settings[id].pumps.length > index
                      ? parent.settings.nutrition.nutrient_settings[id].pumps[index].type
                      : -1
                    : parent.settings.nutrition.pumps.length > index
                      ? parent.settings.nutrition.pumps[index].type
                      : -1
                }
                controls={controls}
                disabled={!write_access}
                mapping={nutritionPumpTypeMapping}
                onChange={(event) => controls.setValue(`${p_path}.type`, event.target.value)}
              />
            </ValueBox>
            <ValueBox controller={controller} id={'pump_unit'}>
              <Typography gutterBottom>
                <Desc id="pump_unit" />
              </Typography>
              <FlexSelect
                id={`pump[${index}].unit`}
                controller={controller}
                value={pump.unit}
                controller_value={
                  controller.zones
                    ? parent.settings.nutrition.nutrient_settings[id].pumps.length > index
                      ? parent.settings.nutrition.nutrient_settings[id].pumps[index].unit
                      : -1
                    : parent.settings.nutrition.pumps.length > index
                      ? parent.settings.nutrition.pumps[index].unit
                      : -1
                }
                controls={controls}
                mapping={nutritionPumpUnitMapping}
                onChange={(event) => controls.setValue(`${p_path}.unit`, event.target.value)}
                disabled={pump.type === 0 || pump.type >= 2}
              />
            </ValueBox>
          </Grid>
        </Grid>
      </Collapse>
    </SettingGroupGrid>
  );
}
